import type { ColumnDef } from '@tanstack/react-table';


import { Box } from 'ui/box';

import Button from 'components/partials/button/button';
import NotificationPoint from 'components/partials/notification-point/notification-point';

import { getIconByFileType } from 'components/partials/file-uploader/file-uploader.helper';
import { formatDate } from 'helpers/dates-formatter';
import { downloadFile } from 'helpers/download-file';

import Icons from 'assets/icons';

import type { UseCurrentUserResult } from 'hooks/api/use-current-user';

const commonStyles = 'border-r-2 border-white font-semibold';

export interface FilesTableRow {
  id: string | number;
  viewed?: boolean;
  asset: {
    name: string;
    fileName: string;
    fileExt: string;
    description?: string;
    createdDate?: string;
  };
  [key: string]: unknown;
}

type FilesColumnDef = ColumnDef<FilesTableRow, unknown> & {
  headerClassName?: string;
  className?: string;
  isHeaderCentered?: boolean;
};

export const getFilesTableSettings = (
  currentUser: UseCurrentUserResult,
  setLoading: (loading: boolean) => void
): FilesColumnDef[] =>
  (
    [
      {
        id: 'asset.fileExt',
        accessorKey: 'asset.fileExt',
        header: () => <span>FILE TYPE</span>,
        cell: ({
          row: {
            original: { asset, viewed },
          },
        }) => {
          const icon = getIconByFileType(asset.fileExt);

          return (
            <div className="relative w-100">
              {!viewed && <NotificationPoint isNoContent={true} />}
              {icon}
            </div>
          );
        },
        headerClassName: `${commonStyles} rounded-tl-md rounded-bl-md cursor-context-menu`,
        className: 'xs:w-[10%] sm:w-[9%] md:w-[7%] xl:w-[6%]',
        isHeaderCentered: true,
        enableSorting: false,
      },
      {
        id: 'asset.name',
        accessorKey: 'asset.name',
        header: () => <span>FILE NAME</span>,
        cell: (info) => (
          <Box sx={{ width: '100%', pr: 1, fontWeight: 600 }}>
            {info.renderValue() as React.ReactNode}
          </Box>
        ),
        headerClassName: `${commonStyles} pl-4 cursor-pointer`,
        className: 'pl-4 xs:w-[19%] sm:w-[16%] md:w-[17%]',
        enableSorting: true,
        sortingFn: 'alphanumeric',
      },
      {
        id: 'asset.description',
        accessorKey: 'asset.description',
        header: () => <span>DESCRIPTION</span>,
        cell: (info) => <span>{info.renderValue() as React.ReactNode}</span>,
        headerClassName: `${commonStyles} pl-4 cursor-context-menu`,
        className: 'pl-4 xs:w-[21%] sm:w-[20%] lg:w-[20%]',
        enableSorting: false,
      },
      {
        id: 'asset.createdDate',
        accessorKey: 'asset.createdDate',
        header: () => <span>UPLOAD DATE</span>,
        cell: (info) =>
          formatDate(info.row.original.asset.createdDate, 'MMM D, YYYY h:mm A'),
        headerClassName: `${commonStyles} pl-4 cursor-pointer`,
        className: 'pl-4 xs:w-[15%]',
        enableSorting: true,
        sortingFn: 'alphanumeric',
      },
      {
        id: 'button-cell',
        enableSorting: false,
        cell: ({
          row: {
            original: { id, asset },
          },
        }) => (
          <Button
            onClick={() =>
              downloadFile({
                setLoading,
                currentUser,
                fileId: id,
                fileName: `${asset.fileName}.${asset.fileExt}`,
              } as never)
            }
            variant="brand-filled"
            size="sm"
            iconBefore={<Icons.Download aria-hidden="true" />}
            className="mx-auto text-xs md:text-sm"
            data-testid="download-file-button"
          >
            Download
          </Button>
        ),
        headerClassName: 'pl-4 h-12 rounded-tr-md rounded-br-md pl-4',
        className:
          'text-center xs:w-[16%] sm:w-[15%] md:w-[12%] lg:w-[10%] xl:w-[9%]',
      },
    ] as Array<FilesColumnDef | false | null | undefined>
  ).filter((item): item is FilesColumnDef => Boolean(item));
